// useUserSync — three live subscriptions for the learner view.
//
//   audios : released==true, orderBy releasedAt desc (newest first)
//   markers: userId == learnerUserId (parent metadata) + pins subcollection
//            scoped to this learner (PR-B1: dual-shape)
//   regions: open==true, orderBy order asc
//
// PR-B1 (Day 13): each marker now has a `pins: PinWithId[]` array. If the
// parent doc has no pins subcollection yet (Day 12 legacy data), we
// synthesize one from the parent's lat/lng/clue so existing learners keep
// seeing markers during the migration window.

import {
  type AudioDoc,
  type FragmentDoc,
  type MarkerDoc,
  type MessageDoc,
  type PinDoc,
  type RegionDoc,
  type WithId,
  allPinsQuery,
  audiosCol,
  markersCol,
  regionsCol,
  useFirestoreCollection,
} from "@goodbye/firebase";
import { orderBy, query, where } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import {
  deleteFragmentFromCache,
  readFragmentsCache,
  writeFragmentsCache,
} from "../lib/fragmentCache";
import {
  deleteMessageFromCache,
  readMessagesCache,
  writeMessagesCache,
} from "../lib/messageCache";
import { useFragments } from "./useFragments";
import { useMessages } from "./useMessages";

export type MarkerWithPins = WithId<MarkerDoc> & {
  pins: WithId<PinDoc>[];
};

export interface UserSyncState {
  audios: WithId<AudioDoc>[];
  markers: MarkerWithPins[];
  regions: WithId<RegionDoc>[];
  messages: WithId<MessageDoc>[];
  fragments: WithId<FragmentDoc>[];
  /** Separate flag so the messages tab can show its own skeleton
   *  without delaying audio / map rendering. */
  messagesLoading: boolean;
  /** Same separation for fragments tab. */
  fragmentsLoading: boolean;
  loading: boolean;
  error: Error | null;
}

const EMPTY: UserSyncState = {
  audios: [],
  markers: [],
  regions: [],
  messages: [],
  fragments: [],
  messagesLoading: false,
  fragmentsLoading: false,
  loading: true,
  error: null,
};

function legacyPinFromMarker(m: WithId<MarkerDoc>): WithId<PinDoc> | null {
  if (typeof m.lat !== "number" || typeof m.lng !== "number") return null;
  return {
    id: "legacy",
    userId: m.userId,
    regionId: m.regionId,
    lat: m.lat,
    lng: m.lng,
    clue: m.clue ?? "（無提示）",
    placedBy: m.placedBy,
    placedAt: m.placedAt,
    foundAt: m.foundAt,
  };
}

export function useUserSync(learnerUserId: string | null, cohortId: string): UserSyncState {
  // Fetch all 15 audios; filter + sort client-side per cohort.
  // This avoids a compound index and lets the legacy "released" fallback work
  // transparently for 2025 cohort without any data migration.
  const audiosQ = useMemo(() => audiosCol(), []);

  const markersQ = useMemo(() => {
    if (!learnerUserId) return null;
    return query(markersCol(), where("userId", "==", learnerUserId));
  }, [learnerUserId]);

  // Pins for THIS learner only (Codex P2#2: don't subscribe to all 75+ pins).
  const pinsQ = useMemo(() => {
    if (!learnerUserId) return null;
    return query(allPinsQuery(), where("userId", "==", learnerUserId));
  }, [learnerUserId]);

  // 2026-05-30:載入「全部」區(不再只拿 open)。未開放區要在學員的地圖
  // 切換器裡顯示成「區域未開放」(MapView 遮名 + 未開放遮罩)。單欄 orderBy
  // 用 auto index,無 composite 需求。
  const regionsQ = useMemo(
    () => query(regionsCol(), orderBy("order", "asc")),
    [],
  );

  const audios = useFirestoreCollection(audiosQ);

  // Per-cohort audio visibility: if the audio has cohortReleased[cohortId], use
  // that; otherwise fall back to the global released boolean (2025 legacy).
  const visibleAudios = useMemo(
    () =>
      audios.data
        .filter((a) =>
          a.cohortReleased && cohortId in a.cohortReleased
            ? a.cohortReleased[cohortId] === true
            : a.released === true,
        )
        .sort((a, b) => {
          const aTs =
            (a.cohortReleasedAt?.[cohortId] ?? a.releasedAt)?.toMillis() ?? 0;
          const bTs =
            (b.cohortReleasedAt?.[cohortId] ?? b.releasedAt)?.toMillis() ?? 0;
          return bTs - aTs;
        }),
    [audios.data, cohortId],
  );

  const markers = useFirestoreCollection(markersQ);
  const pins = useFirestoreCollection(pinsQ);
  const regions = useFirestoreCollection(regionsQ);
  const liveMessages = useMessages(learnerUserId);
  const liveFragments = useFragments(learnerUserId);

  // IndexedDB-backed message cache: hydrate on mount so offline launches
  // paint instantly; mirror every live update back to IDB so the next
  // cold start is fresh.
  const [cachedMessages, setCachedMessages] = useState<WithId<MessageDoc>[]>(
    [],
  );
  useEffect(() => {
    void readMessagesCache().then((list) => {
      if (list.length > 0) setCachedMessages(list);
    });
  }, []);
  useEffect(() => {
    if (liveMessages.loading) return;
    void writeMessagesCache(liveMessages.messages);
  }, [liveMessages.messages, liveMessages.loading]);

  // Same pattern for fragments — IDB-backed cache for cold offline paint.
  const [cachedFragments, setCachedFragments] = useState<
    WithId<FragmentDoc>[]
  >([]);
  useEffect(() => {
    void readFragmentsCache().then((list) => {
      if (list.length > 0) setCachedFragments(list);
    });
  }, []);
  useEffect(() => {
    if (liveFragments.loading) return;
    void writeFragmentsCache(liveFragments.fragments);
  }, [liveFragments.fragments, liveFragments.loading]);

  // SW posts when silent message_retracted / fragment_retracted arrives.
  // Drop from IDB cache so a cold reload doesn't briefly resurrect it.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    function onMessage(event: MessageEvent) {
      const data = event.data as {
        kind?: string;
        messageId?: string;
        fragmentId?: string;
      };
      if (data?.kind === "message_retracted" && data.messageId) {
        void deleteMessageFromCache(data.messageId);
        setCachedMessages((prev) =>
          prev.filter((m) => m.id !== data.messageId),
        );
      } else if (data?.kind === "fragment_retracted" && data.fragmentId) {
        void deleteFragmentFromCache(data.fragmentId);
        setCachedFragments((prev) =>
          prev.filter((f) => f.id !== data.fragmentId),
        );
      }
    }
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, []);

  // While listener still loading, prefer cached so UI has something
  // to render. Once live delivers, swap in.
  const messagesForUi = liveMessages.loading
    ? liveMessages.messages.length > 0
      ? liveMessages.messages
      : cachedMessages
    : liveMessages.messages;

  const fragmentsForUi = liveFragments.loading
    ? liveFragments.fragments.length > 0
      ? liveFragments.fragments
      : cachedFragments
    : liveFragments.fragments;

  const markersWithPins: MarkerWithPins[] = useMemo(() => {
    const pinsByMarkerId = new Map<string, WithId<PinDoc>[]>();
    for (const p of pins.data) {
      const key = `${p.userId}_${p.regionId}`;
      const list = pinsByMarkerId.get(key) ?? [];
      list.push(p);
      pinsByMarkerId.set(key, list);
    }
    return markers.data.map((m) => {
      const realPins = pinsByMarkerId.get(m.id) ?? [];
      const finalPins =
        realPins.length > 0
          ? realPins
          : [legacyPinFromMarker(m)].filter(
              (x): x is WithId<PinDoc> => x !== null,
            );
      return { ...m, pins: finalPins };
    });
  }, [markers.data, pins.data]);

  if (!learnerUserId) return EMPTY;

  return {
    audios: visibleAudios,
    markers: markersWithPins,
    regions: regions.data,
    messages: messagesForUi,
    fragments: fragmentsForUi,
    messagesLoading: liveMessages.loading,
    fragmentsLoading: liveFragments.loading,
    loading:
      audios.loading || markers.loading || pins.loading || regions.loading,
    error:
      audios.error ??
      markers.error ??
      pins.error ??
      regions.error ??
      liveMessages.error ??
      liveFragments.error,
  };
}
